import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Plus, Edit, Trash2, Search, Filter, ExternalLink,
  Building2, Users, Leaf, AlertCircle, CheckCircle, Link2
} from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export default function AdminSchemes() {
  const navigate = useNavigate();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPillar, setFilterPillar] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [linkingScheme, setLinkingScheme] = useState(null);
  const [papers, setPapers] = useState([]);
  const [selectedPaperIds, setSelectedPaperIds] = useState([]);
  const [savingLinks, setSavingLinks] = useState(false);

  useEffect(() => {
    fetchPapers();
  }, []);

  const fetchPapers = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/papers/`);
      setPapers(data);
    } catch (error) {
      console.error('Error fetching papers:', error);
    }
  };

  const openLinkPanel = (scheme) => {
    setLinkingScheme(scheme.id);
    setSelectedPaperIds(scheme.linked_paper_ids || []);
    setDeleteConfirm(null);
  };

  const togglePaper = (paperId) => {
    setSelectedPaperIds(prev =>
      prev.includes(paperId) ? prev.filter(id => id !== paperId) : [...prev, paperId]
    );
  };

  const saveLinkedPapers = async (schemeId) => {
    setSavingLinks(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(`${API_BASE_URL}/api/schemes/${schemeId}/linked-papers`,
        { paper_ids: selectedPaperIds },
        { headers: { Authorization: `Bearer ${token}` } });
      setSchemes(schemes.map(s => s.id === schemeId ? { ...s, linked_paper_ids: selectedPaperIds } : s));
      setLinkingScheme(null);
      alert('Linked papers updated successfully!');
    } catch (error) {
      alert('Failed to update links. ' + (error.response?.data?.detail || 'Please try again.'));
    } finally {
      setSavingLinks(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, [filterPillar, filterStatus, searchQuery]);

  const fetchSchemes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPillar) params.append('pillar', filterPillar);
      if (filterStatus) params.append('status', filterStatus);
      if (searchQuery) params.append('search', searchQuery);
      
      const { data } = await axios.get(`${API_BASE_URL}/api/schemes/?${params.toString()}`);
      setSchemes(data);
    } catch (error) {
      console.error('Error fetching schemes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (schemeId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API_BASE_URL}/api/schemes/${schemeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSchemes(schemes.filter(s => s.id !== schemeId));
      setDeleteConfirm(null);
      alert('Scheme deleted successfully!');
    } catch (error) {
      console.error('Error deleting scheme:', error);
      alert('Failed to delete scheme. ' + (error.response?.data?.detail || 'Please try again.'));
    }
  };

  const getPillarColor = (pillarName) => {
    if (pillarName.includes('Economic')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (pillarName.includes('Social')) return 'bg-green-100 text-green-800 border-green-200';
    if (pillarName.includes('Environmental')) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    if (status === 'closed') return <Badge className="bg-red-100 text-red-800">Closed</Badge>;
    if (status === 'pilot') return <Badge className="bg-yellow-100 text-yellow-800">Pilot</Badge>;
    return <Badge>{status}</Badge>;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F]">Scheme Management</h1>
              <p className="text-gray-600 mt-2">Manage government schemes and programs</p>
            </div>
            <Button 
              onClick={() => navigate('/admin/schemes/add')}
              className="bg-[#E87722] hover:bg-[#d66a1a]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Scheme
            </Button>
          </div>

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    type="text"
                    placeholder="Search schemes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Pillar</label>
                <select
                  value={filterPillar}
                  onChange={(e) => setFilterPillar(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">All Pillars</option>
                  <option value="Economic Stability">💼 Economic Stability</option>
                  <option value="Social Equity">🤝 Social Equity</option>
                  <option value="Environmental Resilience">🌱 Environmental Resilience</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full border rounded-md p-2"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pilot">Pilot</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <p className="text-sm text-gray-600">Total Schemes</p>
              <p className="text-2xl font-bold text-[#1E3A5F]">{schemes.length}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {schemes.filter(s => s.status === 'active').length}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-gray-600">Pilot</p>
              <p className="text-2xl font-bold text-yellow-600">
                {schemes.filter(s => s.status === 'pilot').length}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-gray-600">Closed</p>
              <p className="text-2xl font-bold text-red-600">
                {schemes.filter(s => s.status === 'closed').length}
              </p>
            </Card>
          </div>

          {/* Schemes List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading schemes...</p>
            </div>
          ) : schemes.length === 0 ? (
            <Card className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No schemes found</h3>
              <p className="text-gray-600 mb-4">Get started by adding your first scheme</p>
              <Button onClick={() => navigate('/admin/schemes/add')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Scheme
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {schemes.map((scheme) => (
                <Card key={scheme.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={getPillarColor(scheme.primary_pillar)}>
                          {scheme.primary_pillar}
                        </Badge>
                        {getStatusBadge(scheme.status)}
                      </div>
                      
                      <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">
                        {scheme.scheme_name}
                      </h3>
                      
                      <p className="text-sm text-gray-600 mb-3">
                        {scheme.ministry_department} • {scheme.government_level} Government
                        {scheme.launch_year && ` • ${scheme.launch_year}`}
                      </p>
                      
                      <p className="text-gray-700 mb-4 line-clamp-2">
                        {scheme.objective_summary}
                      </p>
                      
                      {/* Use Case Badges */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {scheme.applies_to_msmes && (
                          <Badge variant="outline" className="text-xs">
                            <Building2 className="h-3 w-3 mr-1" />
                            MSME
                          </Badge>
                        )}
                        {scheme.applies_to_ngos && (
                          <Badge variant="outline" className="text-xs">
                            <Users className="h-3 w-3 mr-1" />
                            NGO
                          </Badge>
                        )}
                        {scheme.applies_to_csr_programs && (
                          <Badge variant="outline" className="text-xs">
                            <Leaf className="h-3 w-3 mr-1" />
                            CSR
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {scheme.themes.slice(0, 2).map((theme, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {theme}
                          </span>
                        ))}
                        {scheme.themes.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{scheme.themes.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2 ml-4">
                      {scheme.official_website && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(scheme.official_website, '_blank')}
                          title="Visit Website"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/programs/${scheme.id}`)}
                        title="View Details"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openLinkPanel(scheme)}
                        title="Link Research Papers"
                        data-testid={`link-papers-btn-${scheme.id}`}
                      >
                        <Link2 className="h-4 w-4" />
                        {(scheme.linked_paper_ids || []).length > 0 && (
                          <span className="ml-1 text-xs font-bold text-[#E87722]">{scheme.linked_paper_ids.length}</span>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/schemes/edit/${scheme.id}`)}
                        title="Edit Scheme"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(scheme.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete Scheme"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Link Papers Panel */}
                  {linkingScheme === scheme.id && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`link-papers-panel-${scheme.id}`}>
                      <p className="text-sm font-medium text-blue-900 mb-3">
                        <Link2 className="h-4 w-4 inline mr-1" />
                        Link research papers to this scheme (curated links appear first on the scheme page)
                      </p>
                      {papers.length === 0 ? (
                        <p className="text-sm text-gray-600">No research papers available</p>
                      ) : (
                        <div className="max-h-60 overflow-y-auto space-y-2 mb-3 bg-white rounded-md p-3 border">
                          {papers.map((paper) => (
                            <label key={paper.id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={selectedPaperIds.includes(paper.id)}
                                onChange={() => togglePaper(paper.id)}
                                className="mt-1"
                                data-testid={`link-paper-checkbox-${paper.id}`}
                              />
                              <span>
                                <span className="font-medium">{paper.title}</span>
                                <span className="text-gray-500"> — {paper.theme}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" disabled={savingLinks} onClick={() => saveLinkedPapers(scheme.id)}
                          className="bg-[#1E3A5F] hover:bg-[#2d5a8f]" data-testid={`save-links-btn-${scheme.id}`}>
                          {savingLinks ? 'Saving...' : `Save Links (${selectedPaperIds.length})`}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setLinkingScheme(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Delete Confirmation */}
                  {deleteConfirm === scheme.id && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm font-medium text-red-800 mb-3">
                        Are you sure you want to delete this scheme? This action cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(scheme.id)}
                        >
                          Yes, Delete
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
